import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import FinancialSummary from "./components/FinancialSummary";
import {
  BookOpen, Users, Calendar, FileText, Mail, ShoppingBag, UserCircle2,
  Wrench, Landmark, Megaphone, ClipboardCheck, Settings, PalmtreeIcon,
  Sparkles, ExternalLink, AlertTriangle, GraduationCap, Percent
} from "lucide-react";

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-2xl shadow-sm p-4 flex items-center gap-3">
    <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center text-white shrink-0`}>
      <Icon size={18} />
    </div>
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-widest text-stone-500 truncate">{label}</div>
      <div className="text-2xl font-serif-display text-stone-800 mt-0.5">{value}</div>
    </div>
  </div>
);

const QuickLink = ({ to, icon: Icon, title, desc, badge, badgeColor = "bg-red-500" }) => (
  <Link
    to={to}
    data-testid={`quick-link-${to.replace(/\W+/g, "-")}`}
    className="group relative bg-white rounded-2xl shadow-sm p-5 flex items-start gap-4 hover:shadow-md hover:-translate-y-0.5 transition-all border border-transparent hover:border-deep-rose/40"
  >
    <div className="w-11 h-11 rounded-xl bg-blush-light text-deep-rose flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
      <Icon size={20} />
    </div>
    <div className="min-w-0 flex-1">
      <div className="font-serif-display text-stone-800 flex items-center gap-2">
        {title}
        {badge != null && badge > 0 && (
          <span className={`inline-flex items-center gap-1 text-[10px] font-medium text-white ${badgeColor} rounded-full px-1.5 py-0.5`}>
            <AlertTriangle size={9} /> {badge}
          </span>
        )}
      </div>
      <div className="text-xs text-stone-500 mt-1 leading-relaxed">{desc}</div>
    </div>
    <ExternalLink size={14} className="text-stone-300 group-hover:text-deep-rose transition-colors" />
  </Link>
);

const Dashboard = () => {
  const { user } = useAuth();
  const restricted = !!user?.restricted;
  const [stats, setStats] = useState(null);
  useEffect(() => {
    if (restricted) return; // teachers don't see financial figures
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, [restricted]);

  // Dedicated, simplified home for restricted admins (teachers): only their allowed shortcuts.
  if (restricted) {
    const firstName = (user?.teacherName || "").trim().split(" ")[0] || "";
    return (
      <div data-testid="teacher-dashboard">
        <h1 className="font-serif-display text-2xl sm:text-3xl text-stone-800">
          Bonjour{firstName ? ` ${firstName}` : ""} 👋
        </h1>
        <p className="text-stone-500 mt-1 text-sm sm:text-base">
          Bienvenue dans votre espace professeur. Retrouvez ici vos accès rapides.
        </p>
        <section className="mt-8">
          <h2 className="text-xs uppercase tracking-widest text-stone-500 mb-3">Mes accès</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <QuickLink to="/admin/attendance" icon={ClipboardCheck} title="Présences"
              desc="Feuille d'émargement de vos cours par date et créneau." />
            <QuickLink to="/admin/broadcast" icon={Megaphone} title="Envoi de mails"
              desc="Envoyer un message aux familles d'un ou plusieurs de vos créneaux." />
            <QuickLink to="/admin/enrollments-by-slot" icon={Users} title="Inscriptions par créneau"
              desc="Vue des élèves inscrits à chacun de vos cours." />
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif-display text-2xl sm:text-3xl text-stone-800">Tableau de bord</h1>
      <p className="text-stone-500 mt-1 text-sm sm:text-base">Vue d&apos;ensemble de l&apos;école, du chiffre d&apos;affaires et des accès rapides.</p>

      {/* Financial summary — same figures shown everywhere in the BO */}
      <section className="mt-8">
        <h2 className="text-xs uppercase tracking-widest text-stone-500 mb-2">Chiffre d&apos;affaires de la saison</h2>
        <FinancialSummary
          attendu={stats?.caAttendu}
          encaisse={stats?.caEncaisse}
          reste={stats?.caReste}
          testid="dashboard-financial-summary"
        />
      </section>

      {/* Counters */}
      <section className="mt-8">
        <h2 className="text-xs uppercase tracking-widest text-stone-500 mb-2">Volumes</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatCard icon={ShoppingBag} label="Commandes" value={stats?.orders ?? "—"} color="bg-coral" />
          <StatCard icon={UserCircle2} label="Familles" value={stats?.families ?? "—"} color="bg-deep-rose" />
          <StatCard icon={GraduationCap} label="Élèves" value={stats?.students ?? "—"} color="bg-purple-500" />
          <StatCard icon={FileText} label="Inscriptions" value={stats?.enrollments ?? "—"} color="bg-orange-500" />
          <StatCard icon={BookOpen} label="Cours" value={stats?.courses ?? "—"} color="bg-sky-500" />
        </div>
      </section>

      {/* Quick access */}
      <section className="mt-10">
        <h2 className="text-xs uppercase tracking-widest text-stone-500 mb-3">Accès rapides</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <QuickLink to="/admin/orders" icon={ShoppingBag} title="Commandes"
            desc="Suivre les commandes, paiements, générer des liens de règlement." />
          <QuickLink to="/admin/crm" icon={UserCircle2} title="Familles (CRM)"
            desc="Fiche famille détaillée, enfants, historique, contact." />
          <QuickLink to="/admin/sepa-diagnostic" icon={Wrench} title="Diagnostic SEPA"
            desc="Vérifier les mandats SEPA et rattacher/relancer les abonnements."
            badge={stats?.sepaStuck} />
          <QuickLink to="/admin/accounting" icon={Landmark} title="Comptabilité"
            desc="Suivi chèques, export Excel, bordereaux de remise." />
          <QuickLink to="/admin/broadcast" icon={Megaphone} title="Mailing groupé"
            desc="Envoyer un message aux familles d&apos;un ou plusieurs créneaux." />
          <QuickLink to="/admin/attendance" icon={ClipboardCheck} title="Présences"
            desc="Feuille d&apos;émargement des cours par date et créneau." />
          <QuickLink to="/admin/enrollments-by-slot" icon={Users} title="Effectifs par créneau"
            desc="Vue synthétique des inscriptions par cours et par jour." />
          <QuickLink to="/admin/settings/finance" icon={Percent} title="Réglages financiers"
            desc="Tarifs, remises, cotisation, licence FFD, frais de dossier." />
          <QuickLink to="/admin/settings/site" icon={Settings} title="Réglages du site"
            desc="Saison, textes, dates clés, contenus publics." />
          <QuickLink to="/admin/stages" icon={PalmtreeIcon} title="Stages vacances"
            desc="Gérer les stages, créneaux et inscriptions." />
          <QuickLink to="/admin/contacts" icon={Mail} title="Messages reçus"
            desc="Formulaire de contact et demandes de niveau." />
          <QuickLink to="/admin/gallery" icon={Sparkles} title="Galerie & médias"
            desc="Photos du site public et images d&apos;illustration." />
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
